import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/layout/Layout";

import Home from "@/pages/Home";
import ProductPage from "@/components/product/ProductPage";
import { PRODUCT_PAGES } from "@/data/products";
import HRGlas from "@/pages/HRGlas";
import IsdeSubsidieKozijnen from "@/pages/IsdeSubsidieKozijnen";
import ROUTE_MATRIX from "@/data/routeMatrix.json";
import Montage from "@/pages/Montage";
import Werkwijze from "@/pages/Werkwijze";
import Projecten from "@/pages/Projecten";
import Inspiratie from "@/pages/Inspiratie";
import Contact from "@/pages/Contact";
import OverOns from "@/pages/OverOns";
import Werkgebied from "@/pages/Werkgebied";
import { CityRoute } from "@/pages/CityPage";
import ProjectDetail from "@/pages/ProjectDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import VeelgesteldeVragen from "@/pages/VeelgesteldeVragen";
import ReviewSubmit from "@/pages/ReviewSubmit";
import KostenZuidHolland from "@/pages/KostenZuidHolland";
import Privacy from "@/pages/Privacy";
import GrotePuien from "@/pages/GrotePuien";
import AluminiumOfKunststof from "@/pages/AluminiumOfKunststof";
import OfferteAanvragen from "@/pages/OfferteAanvragen";
import OfferteBedankt from "@/pages/OfferteBedankt";
import ProfileSystemPage from "@/pages/ProfileSystemPage";
import OfferteVergelijken from "@/pages/OfferteVergelijken";
import OfferteVergelijkenBedankt from "@/pages/OfferteVergelijkenBedankt";

// Beheerpagina's apart laden: bezoekers van de website downloaden de beheercode (editor, slepen, enz.) niet.
const adminPage = (load) => {
  const Page = lazy(load);
  return (props) => <Suspense fallback={<div className="min-h-screen bg-slate-50" data-testid="admin-page-loading" />}><Page {...props} /></Suspense>;
};
const AdminIsde = adminPage(() => import("@/pages/AdminIsde"));
const AdminInspiration = adminPage(() => import("@/pages/AdminInspiration"));
const AdminTexts = adminPage(() => import("@/pages/AdminTexts"));
const AdminFaqs = adminPage(() => import("@/pages/AdminFaqs"));
const AdminKennisbank = adminPage(() => import("@/pages/AdminKennisbank"));
const AdminArticleEditor = adminPage(() => import("@/pages/AdminArticleEditor"));
const AdminArticlePreview = adminPage(() => import("@/pages/AdminArticlePreview"));
const AdminCities = adminPage(() => import("@/pages/AdminCities"));
const AdminCityEditor = adminPage(() => import("@/pages/AdminCityEditor"));
const AdminCityPreview = adminPage(() => import("@/pages/AdminCityPreview"));
const AdminLogin = adminPage(() => import("@/pages/AdminLogin"));
const AdminDashboard = adminPage(() => import("@/pages/AdminDashboard"));
const AdminLeadDetail = adminPage(() => import("@/pages/AdminLeadDetail"));
const AdminProjects = adminPage(() => import("@/pages/AdminProjects"));
const AdminOffers = adminPage(() => import("@/pages/AdminOffers"));
const AdminReviews = adminPage(() => import("@/pages/AdminReviews"));
const AdminPricing = adminPage(() => import("@/pages/AdminPricing"));
const AdminSecurity = adminPage(() => import("@/pages/AdminSecurity"));
const AdminReports = adminPage(() => import("@/pages/AdminReports"));
const AdminMedia = adminPage(() => import("@/pages/AdminMedia"));
const AdminCatalog = adminPage(() => import("@/pages/AdminCatalog"));
const AdminMessages = adminPage(() => import("@/pages/AdminMessages"));

// Doorverwijzing binnen de app: campagneparameters (utm_*, gclid, gbraid, wbraid) en #anker blijven behouden.
const RedirectKeepQuery = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Vaste 301-doorverwijzingen (routeMatrix.json); op productie ook als statische doorverwijspagina (prerender) */}
          {ROUTE_MATRIX.redirects.map((r) => <Route key={r.from} path={r.from} element={<RedirectKeepQuery to={r.to} />} />)}

          {/* Productpagina's V3 (data/products) */}
          {PRODUCT_PAGES.map((p) => (
            <Route key={p.path} path={p.path} element={<ProductPage key={p.path} page={p} />} />
          ))}
          <Route path="/grote-puien" element={<GrotePuien />} />
          <Route path="/aluminium-of-kunststof-kozijnen" element={<AluminiumOfKunststof />} />

          <Route path="/hr++-glas-isolatieglas" element={<HRGlas />} />

          {/* 301-style redirects voor oude URL's → SEO continuity */}

          {/* Overige pagina's */}
          <Route path="/montage" element={<Montage />} />
          <Route path="/werkwijze" element={<Werkwijze />} />
          <Route path="/isde-subsidie-kozijnen" element={<IsdeSubsidieKozijnen />} />
          <Route path="/profielsystemen/:slug" element={<ProfileSystemPage />} />
          <Route path="/projecten" element={<Projecten />} />
          <Route path="/projecten/inspiratie" element={<Inspiratie />} />
          <Route path="/projecten/:slug" element={<ProjectDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/offerte-aanvragen" element={<OfferteAanvragen />} />
          <Route path="/offerte-bedankt" element={<OfferteBedankt />} />
          <Route path="/offerte-vergelijken" element={<OfferteVergelijken />} />
          <Route path="/offerte-vergelijken/bedankt" element={<OfferteVergelijkenBedankt />} />
          <Route path="/over-ons" element={<OverOns />} />
          <Route path="/werkgebied" element={<Werkgebied />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/kosten-kunststof-kozijnen-zuid-holland" element={<KostenZuidHolland />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/veelgestelde-vragen" element={<VeelgesteldeVragen />} />
          <Route path="/review/:token" element={<ReviewSubmit />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/leads" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/leads/:id" element={<AdminLeadDetail />} />
          <Route path="/admin/projects" element={<AdminProjects />} />
          <Route path="/admin/inspiration" element={<AdminInspiration />} />
          <Route path="/admin/texts" element={<AdminTexts />} />
          <Route path="/admin/faqs" element={<AdminFaqs />} />
          <Route path="/admin/kennisbank" element={<AdminKennisbank />} />
          <Route path="/admin/kennisbank/:id" element={<AdminArticleEditor />} />
          <Route path="/admin/kennisbank/:id/voorbeeld" element={<AdminArticlePreview />} />
          <Route path="/admin/werkgebied" element={<AdminCities />} />
          <Route path="/admin/werkgebied/:id" element={<AdminCityEditor />} />
          <Route path="/admin/werkgebied/:id/voorbeeld" element={<AdminCityPreview />} />
          <Route path="/admin/offers" element={<AdminOffers />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/pricing" element={<AdminPricing />} />
          <Route path="/admin/media" element={<AdminMedia />} />
          <Route path="/admin/catalog" element={<AdminCatalog />} />
          <Route path="/admin/isde" element={<AdminIsde />} />
          <Route path="/admin/security" element={<AdminSecurity />} />
          <Route path="/admin/rapporten" element={<AdminReports />} />
          <Route path="/admin/berichten" element={<AdminMessages />} />
          {/* Plaatspagina's uit Beheer → Werkgebied; anders 'niet gevonden' of een goedgekeurde doorverwijzing */}
          <Route path="*" element={<CityRoute />} />
        </Routes>
      </Layout>
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}

export default App;
